import type { AutomobileModel } from '../types/automobile';

/** Kart ve listelerde gösterilecek tam ad */
export function getAutomobileModelDisplayName(model: AutomobileModel): string {
  return model.displayName?.trim() || model.name;
}

/** React list key — aynı isimli nesilleri ayırır */
export function getAutomobileModelKey(model: AutomobileModel, index?: number): string {
  return [
    model.name,
    model.generationCode ?? '',
    model.variantLabel ?? '',
    model.yearFrom ?? '',
    model.yearFromMonth ?? '',
    model.bodyType,
    index ?? '',
  ].join('|');
}

/** Supabase (brand_id, model_key) — nesil/yıl ile benzersiz */
export function getAutomobileModelStableKey(brandSlug: string, model: AutomobileModel): string {
  const name = normalizeAutomobileModelDisplayName(brandSlug, model.name || '');
  const body = model.bodyType || '';
  const gen = (model.generationCode || '').trim().toLowerCase().replace(/\s+/g, '');
  const variant = (model.variantLabel || '').trim().toLowerCase();
  const yFrom = model.yearFrom ?? '';
  const mFrom = model.yearFromMonth ?? '';
  const yTo = model.yearTo ?? '';
  const display = (model.displayName || '').trim().toLowerCase();

  if (gen || variant || yFrom !== '' || mFrom !== '' || yTo !== '') {
    return [brandSlug, name, gen, variant, yFrom, mFrom, yTo, body].join('|');
  }
  if (display) {
    return [brandSlug, name, display, body].join('|');
  }
  return [brandSlug, name, body].join('|');
}

/** Çift kayıt tespiti — displayName varsa isim yazımından bağımsız */
export function getAutomobileModelDuplicateKey(brandSlug: string, model: AutomobileModel): string {
  const name = normalizeAutomobileModelDisplayName(brandSlug, model.name || '');
  const body = model.bodyType || '';
  const gen = (model.generationCode || '').trim().toLowerCase().replace(/\s+/g, '');
  const variant = (model.variantLabel || '').trim().toLowerCase();
  const yFrom = model.yearFrom ?? '';
  const mFrom = model.yearFromMonth ?? '';
  const yTo = model.yearTo ?? '';
  const display = (model.displayName || '').trim().toLowerCase();

  if (display) {
    return [brandSlug, body, display].join('|');
  }
  return [brandSlug, name, gen, variant, yFrom, mFrom, yTo, body].join('|');
}

/** Marka sayfasında model gruplama — C Serisi / C-Serisi tek grupta */
export function getAutomobileModelGroupKey(brandSlug: string, model: AutomobileModel): string {
  return normalizeAutomobileModelDisplayName(brandSlug, model.name).toLowerCase();
}

const GLOBAL_NAME_ALIASES: Record<string, string> = {
  'rav 4': 'RAV4',
  rav4: 'RAV4',
  'cr v': 'CR-V',
  crv: 'CR-V',
  'cr-z': 'CR-Z',
  'cr z': 'CR-Z',
  ecosport: 'EcoSport',
  '500l': '500L',
  '500x': '500X',
};

const BRAND_NAME_ALIASES: Record<string, Record<string, string>> = {
  'mercedes-benz': {
    'a serisi': 'A-Serisi',
    'b serisi': 'B-Serisi',
    'c serisi': 'C-Serisi',
    'e serisi': 'E-Serisi',
    's serisi': 'S-Serisi',
    'g serisi': 'G-Serisi',
    'r serisi': 'R-Serisi',
    'm serisi': 'ML-Serisi',
    'cla serisi': 'CLA-Serisi',
    gla: 'GLA-Serisi',
    glb: 'GLB-Serisi',
    glc: 'GLC-Serisi',
    gle: 'GLE-Serisi',
    gls: 'GLS-Serisi',
    gl: 'GL-Serisi',
    cls: 'CLS-Serisi',
    cla: 'CLA-Serisi',
    clc: 'CLC-Serisi',
    cle: 'CLE-Serisi',
    clk: 'CLK-Serisi',
    eqa: 'EQA-Serisi',
    eqb: 'EQB-Serisi',
    eqc: 'EQC-Serisi',
    eqe: 'EQE-Serisi',
    eqs: 'EQS-Serisi',
    slk: 'SLK-Serisi',
    slc: 'SLC-Serisi',
    sl: 'SL-Serisi',
    'amg gt': 'AMG GT',
    'sls amg': 'SLS AMG',
  },
  bmw: {
    i3: 'i3',
    i4: 'i4',
    i5: 'i5',
    i7: 'i7',
    i8: 'i8',
    ix: 'iX',
    ix1: 'iX1',
    ix2: 'iX2',
    ix3: 'iX3',
  },
  audi: {
    '80 serisi': '80',
    '90 serisi': '90',
    '100 serisi': '100',
    '200 serisi': '200',
  },
};

function normalizeMercedesSerisiName(trimmed: string, lower: string): string {
  const hyphenated = lower.match(/^([a-z0-9]+)-serisi$/);
  if (hyphenated) return `${hyphenated[1].toUpperCase()}-Serisi`;
  const spaced = lower.match(/^([a-z0-9]+)\s+serisi$/);
  if (spaced) return `${spaced[1].toUpperCase()}-Serisi`;
  return trimmed;
}

/** scripts/automobile-model-name-normalize.mjs ile uyumlu */
export function normalizeAutomobileModelDisplayName(brandSlug: string, name: string): string {
  const trimmed = String(name || '').trim();
  if (!trimmed) return trimmed;

  const lower = trimmed.toLowerCase().replace(/\s+/g, ' ');
  const brandMap = BRAND_NAME_ALIASES[brandSlug];
  if (brandMap?.[lower]) return brandMap[lower];
  if (GLOBAL_NAME_ALIASES[lower]) return GLOBAL_NAME_ALIASES[lower];

  if (brandSlug === 'mercedes-benz') return normalizeMercedesSerisiName(trimmed, lower);

  if (brandSlug === 'audi') {
    const numeric = lower.match(/^(\d+)\s+serisi$/);
    if (numeric) return numeric[1];
  }

  if (brandSlug === 'bmw') {
    const bmw = BRAND_NAME_ALIASES.bmw[lower.replace(/\s/g, '')] || BRAND_NAME_ALIASES.bmw[lower];
    if (bmw) return bmw;
  }

  return trimmed;
}

export { dedupeAutomobileModelsByVariant as dedupeAutomobileModels } from './automobileCanonicalBody';

/** displayName + nesil bazlı çift kartları birleştir (Cla Serisi / CLA-Serisi) */
export function dedupeAutomobileModelsStrict(brandSlug: string, models: AutomobileModel[]): AutomobileModel[] {
  const byKey = new Map<string, AutomobileModel>();
  for (const m of models) {
    const key = getAutomobileModelDuplicateKey(brandSlug, m);
    const prev = byKey.get(key);
    if (!prev) {
      byKey.set(key, m);
      continue;
    }
    const score = (x: AutomobileModel) =>
      (x.imageUrl ? 3 : 0) +
      (x.generationCode ? 8 : 0) +
      (x.productionYears?.length ? 4 : 0) +
      (x.imageGeneric === false ? 2 : 0);
    byKey.set(key, score(m) >= score(prev) ? m : prev);
  }
  return [...byKey.values()];
}

export function getAutomobileModelSubtitle(model: AutomobileModel, brandName: string): string {
  const parts: string[] = [brandName, model.bodyType];
  if (model.generationCode && !model.displayName?.includes(model.generationCode)) {
    parts.push(model.generationCode);
  }
  return parts.filter(Boolean).join(' · ');
}

export function getAutomobileModelProductionYears(model: AutomobileModel): number[] {
  if (Array.isArray(model.productionYears) && model.productionYears.length) {
    return Array.from(new Set(model.productionYears)).sort((a, b) => a - b);
  }
  // Only derive a full list if BOTH ends are known.
  if (model.yearFrom && model.yearTo) {
    const start = model.yearFrom;
    const end = model.yearTo;
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return [];
    // Safety cap: avoid rendering hundreds of chips by mistake.
    if (end - start > 60) return [];
    const out: number[] = [];
    for (let y = start; y <= end; y += 1) out.push(y);
    return out;
  }
  // If only the start year is known, show just that year (not the whole range).
  if (model.yearFrom) return [model.yearFrom];
  return [];
}
